<template>
  <v-container max-width="800" class="py-4">
    <v-text-field
      v-model="query"
      label="Search by product name, brand, GTIN or recall ID"
      prepend-inner-icon="mdi-magnify"
      variant="outlined"
      clearable
      hide-details
      :loading="loading"
      class="mb-4"
    />

    <template v-if="trimmedQuery">
      <div v-if="recalls.length > 0" class="d-flex flex-column ga-3">
        <RecallCard
          v-for="recall in recalls"
          :key="recall.id"
          :recall="recall"
        />
      </div>

      <v-pagination
        v-if="totalPages > 1"
        v-model="page"
        :length="totalPages"
        class="mt-4"
      />

      <v-alert
        v-if="!loading && recalls.length === 0"
        type="info"
        variant="tonal"
        class="mt-2"
      >
        No results for "{{ trimmedQuery }}".
      </v-alert>
    </template>

    <div v-else class="d-flex flex-column align-center text-center mt-8">
      <v-icon size="48" color="primary" class="mb-3">mdi-magnify</v-icon>
      <p class="text-body-1">
        Saisissez un nom de produit, une marque ou un code barre pour chercher un rappel produit.
      </p>
    </div>
  </v-container>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch } from 'vue'
import RecallCard, { type RecallItem } from './RecallCard.vue'

const PAGE_SIZE = 25

interface HydraCollection {
  totalItems: number
  member: RecallItem[]
}

export default defineComponent({
  name: 'SearchView',
  components: { RecallCard },
  setup() {
    const query      = ref<string | null>('')
    const recalls    = ref<RecallItem[]>([])
    const loading    = ref(false)
    const totalItems = ref(0)
    const page       = ref(1)

    const trimmedQuery = computed(() => (query.value ?? '').trim())
    const totalPages   = computed(() => Math.ceil(totalItems.value / PAGE_SIZE))

    let debounceTimer: ReturnType<typeof setTimeout> | null = null

    async function fetchRecalls(): Promise<void> {
      if (!trimmedQuery.value) {
        recalls.value    = []
        totalItems.value = 0
        return
      }

      loading.value = true
      try {
        const params = new URLSearchParams({
          search: trimmedQuery.value,
          page:   String(page.value),
        })
        const res = await fetch(`/api/recalls?${params}`, {
          headers: { Accept: 'application/ld+json' },
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data: HydraCollection = await res.json()
        recalls.value    = data.member ?? []
        totalItems.value = data.totalItems ?? 0
      } catch (err) {
        console.error('Search failed', err)
        recalls.value    = []
        totalItems.value = 0
      } finally {
        loading.value = false
      }
    }

    watch(query, () => {
      page.value = 1
      if (debounceTimer) clearTimeout(debounceTimer)
      debounceTimer = setTimeout(fetchRecalls, 350)
    })

    watch(page, fetchRecalls)

    function firstImage(imageUrls: string | null): string | null {
      if (!imageUrls) return null
      const first = imageUrls.split('|')[0].trim()
      return first || null
    }

    function formatDate(iso: string | null): string {
      if (!iso) return ''
      return new Date(iso).toLocaleDateString('fr-FR', {
        day:   '2-digit',
        month: '2-digit',
        year:  'numeric',
      })
    }

    return {
      query,
      trimmedQuery,
      recalls,
      loading,
      page,
      totalPages,
      firstImage,
      formatDate,
    }
  },
})
</script>
